import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    publicKey: String,
    subscribeUrl: String,
    scope: String,
    cookieName: String,
    requireCookie: Boolean
  }

  connect() {
    this.requestInFlight = false
    this.showIfEligible()
  }

  async requestPermission() {
    if (this.requestInFlight) return
    if (!this.isSupported()) return this.hide()

    this.requestInFlight = true

    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.dismiss()
        return
      }

      await this.ensureSubscribed()
      this.hide()
    } catch (error) {
      console.error("Push permission failed:", error)
    } finally {
      this.requestInFlight = false
    }
  }

  dismiss() {
    this.markDismissed()
    this.hide()
  }

  async showIfEligible() {
    if (!this.isSupported()) return
    if (!this.publicKeyValue) return
    if (this.requireCookieValue && !this.cookiePresent()) return this.hide()
    if (this.isDismissed()) return

    if (Notification.permission === "denied") {
      return this.hide()
    }

    if (Notification.permission === "granted") {
      await this.ensureSubscribed()
      return this.hide()
    }

    this.show()
  }

  show() {
    this.element.classList.remove("hidden")
  }

  hide() {
    this.element.classList.add("hidden")
  }

  isSupported() {
    return "Notification" in window && "serviceWorker" in navigator && "PushManager" in window
  }

  cookiePresent() {
    const name = this.cookieNameValue || "push_notifications_test"
    const prefix = `${name}=`
    return document.cookie.split("; ").some((cookie) => cookie.startsWith(prefix))
  }

  async ensureSubscribed() {
    if (!this.publicKeyValue) return

    const registration = await this.registerServiceWorker()
    const existing = await registration.pushManager.getSubscription()

    if (existing) {
      await this.sendSubscription(existing)
      return
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(this.publicKeyValue)
    })

    await this.sendSubscription(subscription)
  }

  async registerServiceWorker() {
    return navigator.serviceWorker.register("/service-worker.js")
  }

  async sendSubscription(subscription) {
    if (!subscription) return

    const payload = subscription.toJSON()
    if (!payload || !payload.endpoint || !payload.keys) return

    const body = {
      subscription: {
        endpoint: payload.endpoint,
        p256dh: payload.keys.p256dh,
        auth: payload.keys.auth,
        user_agent: navigator.userAgent
      }
    }

    await fetch(this.subscribeUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(body)
    })
  }

  subscribeUrl() {
    return this.subscribeUrlValue || "/push_subscriptions"
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.content : ""
  }

  dismissKey() {
    const scope = this.scopeValue || "default"
    return `push_notifications_dismissed_${scope}`
  }

  markDismissed() {
    try {
      sessionStorage.setItem(this.dismissKey(), "1")
    } catch (_) {}
  }

  isDismissed() {
    try {
      return sessionStorage.getItem(this.dismissKey()) === "1"
    } catch (_) {
      return false
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }
}
